import * as React from "react"
import { Container, Row, Col, Table, Spinner, Button } from "react-bootstrap"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map, catchError } from "rxjs/operators"
import { toast } from "react-toastify"
import { InvitationGridRequest } from "../../api/InvitationStatus/Model/InvitationGridRequest"
import { ColumnFilters, InvitationGridResponse } from "../../api/InvitationStatus/Model/InvitationGridResponse"
import InvitationStatusListGet from "../../api/InvitationStatus/InvitationStatusListGet"
import InvitationStatusGridRecord from "../../api/InvitationStatus/Model/InvitationStatusGridRecord"
import TablePagination from "../TablePagination"
import { Filter, Sort } from "../../api/GridRequest"
import ResendInvitationModal from "./ResendInvitationModal"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import AdminSetupModal from "../Invitation/AdminSetupModal"
import { getStatusLabelByKey, InvitationStatus } from "../../api/Invitation/Model/InvitationStatus"
import FormatDate from "../../formatters/FormatDate"
import { CustomizableFilterColumn } from "../ColumnsCustomizeModal/CustomizableColumn"
import { isSiteAdmin } from "../../authentication/isAdminRole"
import { connect } from "react-redux"
import { AppState } from "../../AppState"
import ColumnsCustomizeModal, { loadCustomized } from "../ColumnsCustomizeModal/ColumnsCustomizeModal"
import { StorageKey } from "../../config/Constants"

type Column = CustomizableFilterColumn<keyof InvitationStatusGridRecord, keyof ColumnFilters>

interface InvitationStatusGridProps {
  isSiteAdmin?: boolean
}

interface InvitationStatusGridState {
  data?: InvitationGridResponse
  loading: boolean
  modal: boolean
  modalData?: InvitationStatusGridRecord
  pageNumber: number
  showAdminInvitation: boolean
  customizeModal: boolean,
  columns: Column[],
  sort: Sort
  filters: Filter[]
}

const PageSize = 20
const ShowModalForStatus = [
  InvitationStatus.INVITATION_REQUESTED,
  InvitationStatus.TOO_MANY_ATTEMPT,
  InvitationStatus.PENDING,
  InvitationStatus.EXPIRED
]

const defaultColumns: Column[] = [
  { name: "Name", filter: "CompanyName", label: "Company", visible: true, customize: true },
  { name: "PrimaryRoleTitle", filter: "Role", label: "Role", visible: true, customize: true },
  { name: "UserName", filter: "EmailAddress", label: "Email Address", visible: true, customize: true },
  { name: "ModifiedOn", filter: "InvitationSent", label: "Invite Sent", visible: true, customize: true },
  { name: "EnrollmentStarted", filter: "EnrollmentStarted", label: "Enrollment Started", visible: true, customize: true },
  { name: "Status", filter: "Status", label: "Invite Status", visible: true, customize: true },
  { name: "InActive", filter: "InActive", label: "Active/InActive", visible: true, customize: true },
  { name: "Link", label: "Invite Link", visible: false, customize: true }
]

export class InvitationStatusGrid extends React.PureComponent<InvitationStatusGridProps, InvitationStatusGridState> {
  state: InvitationStatusGridState = {
    loading: false,
    modal: false,
    pageNumber: 1,
    showAdminInvitation: false,
    customizeModal: false,
    columns: loadCustomized(StorageKey.InvitationStatusGridColumns, defaultColumns) as Column[],
    sort: { column: "ModifiedOn", order: "DSC" },
    filters: []
  }

  page$ = new BehaviorSubject<InvitationGridRequest>({
    pageNumber: 1,
    sort: { column: "ModifiedOn", order: "DSC" },
    filters: [],
    pageSize: PageSize
  })

  pageSubscription?: Subscription

  pageChanged = (pageNumber: number) => {
    this.setState({ pageNumber: pageNumber })
    this.page$.next({ ...this.page$.getValue(), pageNumber: pageNumber })
  }

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        InvitationStatusListGet(params).pipe(
          map((result: InvitationGridResponse) => ({ data: result, loading: false })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: InvitationGridResponse }) => {
        if (newState.data) {
          if (newState.data.InActive?.length) {
            newState.data.InActive = newState.data.InActive.map(inactiveString => inactiveString === "true" ? "Inactive" : "Active")
          }
          if (newState.data.Status?.length) {
            newState.data.Status = newState.data.Status.map(status => getStatusLabelByKey(InvitationStatus[status as keyof typeof InvitationStatus]))
          }
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  showModal = (row: InvitationStatusGridRecord) => this.setState({ modal: true, modalData: row })

  hideModal = () => this.setState({ modal: false, modalData: undefined })

  getUserName = (row: InvitationStatusGridRecord) => {
    if (ShowModalForStatus.indexOf(row.Status) >= 0) {
      return <Button variant="link" className="text-left p-0" onClick={() => this.showModal(row)}>{row.UserName}</Button>
    } else {
      return row.UserName
    }
  }

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.setState({ sort: newSort })
    this.page$.next({ ...this.page$.getValue(), sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    this.setState(prevState => {
      const filters = [...prevState.filters]
      const filter = { column: column, values: items }
      let index = filters.findIndex(item => item.column === filter.column)
      if (index >= 0) {
        filters[index] = filter
      } else {
        filters.push(filter)
      }
      return { filters: filters }
    }, () => {
      this.setState({ pageNumber: 1 })
      this.page$.next({ ...this.page$.getValue(), pageNumber: 1, filters: this.state.filters })
    })
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  renderCell = (row: InvitationStatusGridRecord, colName: keyof InvitationStatusGridRecord, key: number) => {
    let keyCell = `${key}_${colName}`
    switch (colName) {
      case "UserName":
        return <td key={keyCell} className="align-text-top">{this.getUserName(row)}</td>
      case "ModifiedOn":
        return <td key={keyCell} className="align-text-top">{FormatDate(row.ModifiedOn)}</td>
      case "EnrollmentStarted":
        return <td key={keyCell} className="align-text-top">{FormatDate(row.EnrollmentStarted)}</td>
      case "Status":
        return <td key={keyCell} className="align-text-top">{getStatusLabelByKey(row.Status)}</td>
      case "InActive":
        return <td key={keyCell} className="align-text-top">{row.InActive ? "InActive" : "Active"}</td>
      case "Link":
        return <td key={keyCell} className="align-text-top"><Button variant="link" onClick={() => this.onGoToInvitationLink(row.Link)} className="text-left p-0">{row.Link}</Button></td>
      default:
        return <td key={keyCell} className="align-text-top">{row[colName]}</td>
    }
  }

  renderDataColumns = (row: InvitationStatusGridRecord) =>
    this.visibleColumns().map((col, i) =>
      this.renderCell(row, col.name as keyof InvitationStatusGridRecord, i))

  renderRows = () => {
    const { data } = this.state
    if (data?.Records?.length) {
      return data?.Records?.map((rows: InvitationStatusGridRecord[]) =>
        rows.map((row: InvitationStatusGridRecord, i: number) =>
          <tr key={`row_${i}`}>
            {this.renderDataColumns(row)}
          </tr>))
    } else {
      return <tr><td colSpan={this.visibleColumns().length + 1}>No Records Found</td></tr>
    }
  }

  renderHeader = () => this.visibleColumns().map((column: Column) => {
    const { sort, filters } = this.page$.value
    const { data } = this.state
    const { name, label, filter } = column
    return (
      <th key={name} className="text-nowrap font-weight-normal">
        {data && filter && filter in data &&
          <ColumnHeaderFilter
            uppercase={false}
            title={label || name}
            filteredItems={filters.find(item => item.column === filter)?.values || []}
            sort={sort.column === name ? sort.order === "asc" ? "asc" : "desc" : undefined}
            items={data[filter as keyof ColumnFilters]?.filter(item => !!item) || []}
            onSort={(order?: string) => this.sortGrid(name, order)}
            onFilter={(items: string[]) => filter && this.filterGrid(filter, items)}
          />}
        {(!data || !filter) && (label || name)}
      </th>
    )
  })

  deleteInvitation = () => {
    this.hideModal()
    this.page$.next({ ...this.page$.getValue() })
  }

  resendInvitation = () => {
    this.hideModal()
    this.page$.next({ ...this.page$.getValue() })
  }

  handleInvitationSetupModalClose = (hasUpdates: boolean) => {
    this.setState({ showAdminInvitation: false })
    if (hasUpdates) {
      this.pageChanged(1)
    }
  }

  onGoToInvitationLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  render() {
    const { isSiteAdmin } = this.props
    return (
      <AppPageWrapper showNav parentClassName={"align-items-center"}>
        <ResendInvitationModal show={this.state.modal} modalData={this.state.modalData} onHide={this.hideModal}
          onDeleteInvitation={this.deleteInvitation} onResendInvitation={this.resendInvitation} />
        <Container fluid className="pt-5 grid-container">
          <Row className="pb-2">
            <Col className="text-md-right mt-2 mt-md-0">
              {isSiteAdmin && <>
                <ColumnsCustomizeModal
                  show={this.state.customizeModal}
                  columns={this.state.columns}
                  storeKey={StorageKey.InvitationStatusGridColumns}
                  onClose={() => this.setState({ customizeModal: false })}
                  onSave={columns => this.setState({ columns: columns as Column[], customizeModal: false })} />
                <Button variant="secondary" className="mr-1" size="sm" onClick={() => this.setState({ customizeModal: true })}>Customize</Button>
                <Button variant="primary" className="mr-1" size="sm" onClick={() => this.setState({ showAdminInvitation: true })}>Add New Site Administrator</Button>
              </>}
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead className="thead-light bordered responsive">
                  <tr>
                    {this.renderHeader()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={this.visibleColumns().length + 1}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data && !this.state.loading && <React.Fragment>
            <span className="total-records">Total records: {this.state.data.Total}</span>
            <TablePagination pageNumber={this.state.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />
          </React.Fragment>}
        </Container>
        { this.state.showAdminInvitation && <AdminSetupModal show={this.state.showAdminInvitation} onClose={this.handleInvitationSetupModalClose} />}
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): InvitationStatusGridProps {
  return {
    isSiteAdmin: isSiteAdmin(state)
  }
}
const ConnectedInvitationStatusGrid = connect(mapStateToProps, null)(InvitationStatusGrid)
export default ConnectedInvitationStatusGrid
